import type { SidebarItemNode } from "./sidebar-config";

export type SidebarPresentationGroup = {
  key: "workspace" | "operations" | "commerce" | "management";
  label: "Workspace" | "Operations" | "Commerce" | "Management";
  items: SidebarItemNode[];
};

export type MobileSidebarGroupKey =
  | "sales"
  | "stock"
  | "operations"
  | "commerce"
  | "management"
  | "reporting"
  | "settings";

export type MobileSidebarGroup = {
  key: MobileSidebarGroupKey;
  label: string;
  items: SidebarItemNode[];
};

const GROUPS = [
  { key: "workspace", label: "Workspace" },
  { key: "operations", label: "Operations" },
  { key: "commerce", label: "Commerce" },
  { key: "management", label: "Management" },
] as const;

const WORKSPACE_SECTIONS = new Set(["dashboard", "tasks", "operations", "account"]);
const OPERATIONS_SECTIONS = new Set([
  "inventory", "purchase_orders", "packing", "print_pack", "suppliers",
  "consignment", "consignment_sync", "warehouse", "returns", "scrap", "hr",
]);
const COMMERCE_SECTIONS = new Set([
  "sales", "messages", "customers", "channels", "social", "tiktok_shop",
  "commerce", "dropshipping", "pre_orders",
]);
const MANAGEMENT_SECTIONS = new Set([
  "integrations", "expenses", "stationery", "reports", "admin", "settings",
  "tenants", "platform", "notifications", "ai_agents",
]);

const MOBILE_GROUPS: Array<{ key: MobileSidebarGroupKey; label: string }> = [
  { key: "sales", label: "Sales" },
  { key: "stock", label: "Stock" },
  { key: "operations", label: "Operations" },
  { key: "commerce", label: "Commerce" },
  { key: "management", label: "Management" },
  { key: "reporting", label: "Reporting" },
  { key: "settings", label: "Settings" },
];

const MOBILE_GROUP_SECTIONS: Record<MobileSidebarGroupKey, Set<string>> = {
  sales: new Set(["sales", "messages", "customers", "channels", "social", "tiktok_shop"]),
  stock: new Set(["inventory", "purchase_orders", "pre_orders", "suppliers", "returns", "scrap", "warehouse"]),
  operations: new Set(["packing", "print_pack", "tasks", "operations", "consignment", "consignment_sync", "hr", "stationery", "documents"]),
  commerce: new Set(["commerce", "dropshipping"]),
  management: new Set(["integrations", "expenses", "admin", "platform", "tenants", "ai_agents"]),
  reporting: new Set(["reports", "analytics", "sales_performance", "product_performance", "forecasting", "customer_insights"]),
  settings: new Set(["settings", "account", "notifications", "help"]),
};

function classifyMobile(item: SidebarItemNode): MobileSidebarGroupKey {
  const section = item.sectionKey?.toLowerCase() ?? "";
  const href = item.href.toLowerCase();
  for (const group of MOBILE_GROUPS) {
    if (MOBILE_GROUP_SECTIONS[group.key].has(section)) return group.key;
  }
  if (/^\/(sales|messages|customers|channels|social|tiktok-shop)/.test(href)) return "sales";
  if (/^\/(inventory|purchase-orders|pre-orders|suppliers|returns|warehouse)/.test(href)) return "stock";
  if (/^\/(packing|tasks|operations|consignment|hr|stationery|documents)/.test(href)) return "operations";
  if (/^\/commerce/.test(href)) return "commerce";
  if (/^\/(reports|sales-performance|product-performance|forecasting|customer-insights|analytics)/.test(href)) return "reporting";
  if (/^\/(settings|account|notifications|help)/.test(href)) return "settings";
  // Future/custom server entries remain reachable exactly once.
  return "management";
}

/** Mobile-only adapter. It preserves the server tree nodes and only changes presentation grouping. */
export function groupMobileSidebarItems(items: SidebarItemNode[]): MobileSidebarGroup[] {
  const buckets = new Map(MOBILE_GROUPS.map((group) => [group.key, [] as SidebarItemNode[]]));
  for (const item of items) {
    if (item.href === "/") continue;
    buckets.get(classifyMobile(item))!.push(item);
  }
  return MOBILE_GROUPS
    .map((group) => ({ ...group, items: buckets.get(group.key)! }))
    .filter((group) => group.items.length > 0);
}

/** Return the P&L destination only when it is present beneath the authorised Dashboard node. */
export function getDashboardProfitLossItem(items: SidebarItemNode[]): SidebarItemNode | undefined {
  return items
    .find((item) => item.href === "/" && !item.isHidden)
    ?.children.find((child) => child.href === "/profit-loss" && !child.isHidden);
}

function classify(item: SidebarItemNode): SidebarPresentationGroup["key"] {
  const section = item.sectionKey?.toLowerCase() ?? "";
  const href = item.href.toLowerCase();
  if (WORKSPACE_SECTIONS.has(section) || href === "/" || href.startsWith("/tasks")) return "workspace";
  if (OPERATIONS_SECTIONS.has(section) || /^(\/inventory|\/purchase-orders|\/packing|\/suppliers|\/warehouse|\/returns|\/scrap|\/hr)/.test(href)) return "operations";
  if (COMMERCE_SECTIONS.has(section) || /^(\/sales|\/messages|\/customers|\/channels|\/social|\/tiktok-shop|\/commerce)/.test(href)) return "commerce";
  if (MANAGEMENT_SECTIONS.has(section)) return "management";
  // Unknown and future server-provided modules remain visible exactly once.
  return "management";
}

/** Groups the server-authorised root tree without filtering, cloning or reordering its nodes. */
export function groupSidebarItems(items: SidebarItemNode[]): SidebarPresentationGroup[] {
  const buckets = new Map(GROUPS.map((group) => [group.key, [] as SidebarItemNode[]]));
  for (const item of items) buckets.get(classify(item))!.push(item);
  return GROUPS.map((group) => ({ ...group, items: buckets.get(group.key)! }))
    .filter((group) => group.items.length > 0);
}